import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  capitalize,
  COURSES_CALENDAR_URL_PATH,
  DrawerType,
  ISettingsGroup,
  RideRoutes,
  RoutePaths,
  systemSettingsDtoAtom,
  useDrawers,
  useHaveCustomPermissions,
  useIsMobile,
  usePrefixedRouting,
} from "@ryde/shared";

import { settingsMenuIcons } from "./settings-menu.icons";
import { useCreateSettingsItem } from "./use-create-settings-item";

interface ISettingsData {
  settingsItems: ISettingsGroup[];
}

export const useSettingsData = (): ISettingsData => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { createDefaultMenuItem } = useCreateSettingsItem();
  const { addPrefix } = usePrefixedRouting();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { openDrawer } = useDrawers();
  const {
    hasPermissionViewSupplierPrices,
    hasPermissionReadSupplierPriceRate,
    hasPermissionViewFeedback,
    hasPermissionViewStation,
  } = useHaveCustomPermissions();

  const coursesCalendarMenuHref = useMemo(
    () => addPrefix(`${RoutePaths.COURSES}${COURSES_CALENDAR_URL_PATH}`),
    [addPrefix],
  );

  const settingsItems = useMemo(() => {
    const {
      showCourses,
      useFoods,
      disableShiftsDropdown,
      enableFlights,
      enableComplains,
      enableSupplierPriceRates,
      enableIncidents,
      enableHubsLogic,
    } = systemSettingsDto?.metadata || {};
    const showSupplierPriceRateListMenuItem =
      !!enableSupplierPriceRates && hasPermissionReadSupplierPriceRate;
    const showAdministrativeModeMenuItem =
      !!enableSupplierPriceRates &&
      hasPermissionReadSupplierPriceRate &&
      hasPermissionViewSupplierPrices;
    const sharedMenuItems: ISettingsGroup[] = [
      {
        groupTitle: capitalize(t(`report.plural`)),
        groupItems: [
          createDefaultMenuItem("report", {
            leftIcon: settingsMenuIcons.reports,
          }),
        ],
      },
      {
        groupTitle: t("notification.plural"),
        groupItems: [createDefaultMenuItem("notification")],
      },
      {
        groupTitle: capitalize(t(`supplier.plural`)),
        groupItems: [
          createDefaultMenuItem("supplier"),
          ...(showSupplierPriceRateListMenuItem
            ? [createDefaultMenuItem("supplierPriceRate")]
            : []),
          ...(showAdministrativeModeMenuItem
            ? [
                createDefaultMenuItem("supplierPriceRate", {
                  titleTranslationKey: "general.ADMINISTRATIVE_MODE",
                  buttonModeClickEventHandler: () => {
                    openDrawer({
                      drawerProps: {
                        type: DrawerType.AdministrativeMode,
                      },
                    });
                  },
                }),
              ]
            : []),
          createDefaultMenuItem("driver"),
          createDefaultMenuItem("vehicle"),
          createDefaultMenuItem("vehicleType"),
          createDefaultMenuItem("priceRate"),
          createDefaultMenuItem("reason"),
          createDefaultMenuItem("reasonCategory"),
        ],
      },
      {
        groupTitle: capitalize(t(`user.plural`)),
        groupItems: [createDefaultMenuItem("systemManager")],
      },

      {
        groupTitle: capitalize(t(`site.plural`)),
        groupItems: [createDefaultMenuItem("site")],
      },
      {
        groupTitle: capitalize(t(`passenger.plural`)),
        groupItems: [
          createDefaultMenuItem("passenger", {
            leftIcon: settingsMenuIcons.passengers,
          }),
          createDefaultMenuItem("passengerType"),
        ],
      },
      ...(!disableShiftsDropdown
        ? [
            {
              groupTitle: capitalize(t(`shift.plural`)),
              groupItems: [
                createDefaultMenuItem("shift"),
                createDefaultMenuItem("shiftTemplate"),
              ],
            },
          ]
        : []),
      {
        groupTitle: t(`general.LOCATIONS`),
        groupItems: [
          createDefaultMenuItem("area"),
          createDefaultMenuItem("areasGroup"),
          createDefaultMenuItem("station"),
          ...(enableHubsLogic && hasPermissionViewStation
            ? [
                createDefaultMenuItem("station", {
                  titleTranslationKey: "hub.hubStationsInfo",
                  buttonModeClickEventHandler: () => {
                    openDrawer({
                      drawerProps: {
                        type: DrawerType.HubStationsInfo,
                      },
                    });
                  },
                }),
              ]
            : []),
          createDefaultMenuItem("stationDistance"),
          createDefaultMenuItem("stationRoute"),
        ],
      },
      {
        groupTitle: capitalize(t(`unit.plural`)),
        groupItems: [
          createDefaultMenuItem("unit"),
          createDefaultMenuItem("businessUnit"),
        ],
      },
      ...(enableComplains && hasPermissionViewFeedback
        ? [
            {
              groupTitle: capitalize(t(`feedback.plural`)),
              groupItems: [
                createDefaultMenuItem("feedback", {
                  leftIcon: settingsMenuIcons.feedbacks,
                }),
                createDefaultMenuItem("feedbackSubject"),
                createDefaultMenuItem("feedbackSolution"),
              ],
            },
          ]
        : []),
      ...(enableIncidents
        ? [
            {
              groupTitle: capitalize(t(`incident.plural`)),
              groupItems: [
                createDefaultMenuItem("incident"),
                createDefaultMenuItem("incidentType"),
              ],
            },
          ]
        : []),

      ...(useFoods
        ? [
            {
              groupTitle: capitalize(t(`meal.plural`)),
              groupItems: [
                createDefaultMenuItem("meal"),
                createDefaultMenuItem("foodSupplier"),
              ],
            },
          ]
        : []),
      {
        groupTitle: t(`general.GENERAL`),
        groupItems: [
          createDefaultMenuItem("addition"),
          createDefaultMenuItem("complaint"),
          ...(showCourses
            ? [
                createDefaultMenuItem("course", {
                  leftIcon: settingsMenuIcons.courses,
                }),
                createDefaultMenuItem("course", {
                  href: coursesCalendarMenuHref,
                  titleTranslationKey: "course.coursesCalendarMenu",
                }),
              ]
            : []),
          createDefaultMenuItem("dayType"),
          createDefaultMenuItem("dayGroup"),
          createDefaultMenuItem("journey"),
          createDefaultMenuItem("disableHour"),
          ...(enableFlights ? [createDefaultMenuItem("flight")] : []),
          createDefaultMenuItem("produceButton", {
            leftIcon: settingsMenuIcons.produceButtons,
          }),
          createDefaultMenuItem("dataUpload"),
          createDefaultMenuItem("rideProduction"),
          createDefaultMenuItem("rideType"),
          createDefaultMenuItem("systemTag"),
          createDefaultMenuItem("systemParam"),
          createDefaultMenuItem("systemParam", {
            href: RoutePaths.HELP_TOPICS,
            titleTranslationKey: "helpTopics.title",
          }),
        ],
      },
    ];

    if (!isMobile) {
      return sharedMenuItems;
    }

    const mobileMenuItems: ISettingsGroup[] = [
      {
        groupTitle: capitalize(t(`ride.plural`)),
        groupItems: [
          createDefaultMenuItem("ride", {
            leftIcon: settingsMenuIcons.rides,
          }),
          createDefaultMenuItem("ride", {
            href: RideRoutes.SEARCH_MAP,
            titleTranslationKey: "rideMap.plural",
            leftIcon: settingsMenuIcons.liveMap,
          }),
        ],
      },
      {
        groupTitle: capitalize(t(`booking.plural`)),
        groupItems: [
          createDefaultMenuItem("booking", {
            leftIcon: settingsMenuIcons.bookings,
          }),
        ],
      },
    ];

    return [...mobileMenuItems, ...sharedMenuItems];
  }, [
    t,
    isMobile,
    systemSettingsDto?.metadata,
    hasPermissionViewSupplierPrices,
    hasPermissionReadSupplierPriceRate,
    hasPermissionViewFeedback,
    hasPermissionViewStation,
    createDefaultMenuItem,
    coursesCalendarMenuHref,
    openDrawer,
  ]);

  return {
    settingsItems,
  };
};
